import { useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DocumentStatusBadge } from '@/components/documents/DocumentStatusBadge';
import { Building2, ChevronDown, ChevronRight, FileText, Home, Loader2, Minus, Plus, Search, Settings } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { Toaster } from '@/components/ui/toaster';
import { PaginatedTableControls } from '@/components/ui/paginated-table';
import { emptyPagination, fetchJson, PaginationMeta, RequestSequencer } from '@/lib/jsonPagination';

interface Document {
  id: number;
  name: string;
  description?: string;
  status: string;
}

interface Municipality {
  id: number;
  name: string;
  document_count: number;
}

interface MunicipalityDocumentsIndexProps {
  municipalities: Municipality[];
}

interface DocumentsState {
  documents: Document[];
  pagination: PaginationMeta;
  search: string;
  loading: boolean;
}

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

async function errorMessageFromResponse(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    if (Array.isArray(body?.errors) && body.errors.length > 0) {
      return body.errors.join(' ');
    }
    if (typeof body?.error === 'string' && body.error.trim()) {
      return body.error;
    }
  } catch {
    // ignore parse errors and use fallback
  }
  return fallback;
}

function municipalityRequestSequencer(
  sequencers: MutableRefObject<Map<number, RequestSequencer>>,
  municipalityId: number
) {
  let sequencer = sequencers.current.get(municipalityId);
  if (!sequencer) {
    sequencer = new RequestSequencer();
    sequencers.current.set(municipalityId, sequencer);
  }
  return sequencer;
}

export default function MunicipalityDocumentsIndex({ municipalities }: MunicipalityDocumentsIndexProps) {
  const [municipalitySearch, setMunicipalitySearch] = useState('');
  const [expandedMunicipalityIds, setExpandedMunicipalityIds] = useState<Set<number>>(new Set());
  const [municipalityCounts, setMunicipalityCounts] = useState<Record<number, number>>(
    Object.fromEntries(municipalities.map((municipality) => [municipality.id, municipality.document_count]))
  );
  const [documentsByMunicipality, setDocumentsByMunicipality] = useState<Record<number, DocumentsState>>({});
  const municipalitySequencers = useRef(new Map<number, RequestSequencer>());
  const { toast } = useToast();

  const filteredMunicipalities = useMemo(() => {
    if (!municipalitySearch.trim()) return municipalities;

    const query = municipalitySearch.toLowerCase();
    return municipalities.filter((municipality) => municipality.name.toLowerCase().includes(query));
  }, [municipalities, municipalitySearch]);

  const loadMunicipalityDocuments = async (
    municipalityId: number,
    options: { page?: number; perPage?: number; search?: string } = {}
  ) => {
    const currentState = documentsByMunicipality[municipalityId];
    const page = options.page || 1;
    const perPage = options.perPage || currentState?.pagination.per_page || 25;
    const search = options.search ?? currentState?.search ?? '';
    const params = new URLSearchParams({
      page: String(page),
      per_page: String(perPage)
    });
    if (search.trim()) params.set('search', search.trim());

    setDocumentsByMunicipality((previous) => ({
      ...previous,
      [municipalityId]: {
        documents: previous[municipalityId]?.documents || [],
        pagination: previous[municipalityId]?.pagination || emptyPagination(),
        search,
        loading: true
      }
    }));

    const request = municipalityRequestSequencer(municipalitySequencers, municipalityId).start();
    const result = await fetchJson<{ documents: Document[]; pagination: PaginationMeta }>(
      `/municipality_documents/${municipalityId}/documents.json?${params.toString()}`
    );

    if (!result.ok) {
      if (!request.isCurrent()) return;

      toast({
        title: 'Error',
        description: 'Failed to load documents for this municipality.',
        variant: 'destructive'
      });
      setDocumentsByMunicipality((previous) => ({
        ...previous,
        [municipalityId]: {
          documents: previous[municipalityId]?.documents || [],
          pagination: previous[municipalityId]?.pagination || emptyPagination(),
          search,
          loading: false
        }
      }));
      return;
    }

    if (!request.isCurrent()) return;

    setDocumentsByMunicipality((previous) => ({
      ...previous,
      [municipalityId]: {
        documents: result.data.documents,
        pagination: result.data.pagination,
        search,
        loading: false
      }
    }));
  };

  const toggleMunicipality = (municipalityId: number) => {
    const isExpanded = expandedMunicipalityIds.has(municipalityId);

    setExpandedMunicipalityIds((previous) => {
      const next = new Set(previous);
      if (next.has(municipalityId)) {
        next.delete(municipalityId);
      } else {
        next.add(municipalityId);
      }
      return next;
    });

    if (!isExpanded && !documentsByMunicipality[municipalityId]) {
      loadMunicipalityDocuments(municipalityId);
    }
  };

  const handleDisassociateDocument = async (
    municipalityId: number,
    municipalityName: string,
    documentId: number,
    documentName: string
  ) => {
    if (!confirm(`Remove "${documentName}" from ${municipalityName}?`)) return;

    const response = await fetch(`/api/v1/municipalities/${municipalityId}/documents/${documentId}/disassociate`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      }
    });

    if (!response.ok) {
      toast({
        title: 'Error',
        description: await errorMessageFromResponse(response, 'Failed to disassociate document.'),
        variant: 'destructive'
      });
      return;
    }

    toast({
      title: 'Success',
      description: 'Document disassociated successfully.'
    });
    setMunicipalityCounts((previous) => ({
      ...previous,
      [municipalityId]: Math.max((previous[municipalityId] || 1) - 1, 0)
    }));
    await loadMunicipalityDocuments(municipalityId, {
      page: documentsByMunicipality[municipalityId]?.pagination.current_page || 1
    });
  };

  const handleAssociateDocuments = async (municipalityId: number, documentIds: number[]) => {
    const results = await Promise.allSettled(
      documentIds.map((documentId) =>
        fetch(`/api/v1/municipalities/${municipalityId}/documents/${documentId}/associate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken()
          }
        })
      )
    );
    const fulfilled = results.filter(
      (result): result is PromiseFulfilledResult<Response> => result.status === 'fulfilled'
    );
    const successful = fulfilled.filter((result) => result.value.ok).length;
    const failedCount = results.length - successful;
    const failedResponses = fulfilled.filter((result) => !result.value.ok).map((result) => result.value);

    if (successful === 0) {
      const firstFailure = failedResponses[0];
      toast({
        title: 'Error',
        description: firstFailure
          ? await errorMessageFromResponse(firstFailure, 'Failed to associate documents.')
          : 'Failed to associate documents.',
        variant: 'destructive'
      });
      return;
    }

    if (failedCount > 0) {
      const firstFailure = failedResponses[0];
      const serverMessage = firstFailure
        ? await errorMessageFromResponse(firstFailure, '')
        : '';
      toast({
        title: 'Partial success',
        description: `${successful} associated; ${failedCount} failed.${serverMessage ? ` ${serverMessage}` : ''}`,
        variant: 'destructive'
      });
    } else {
      toast({
        title: 'Success',
        description: `${successful} document${successful === 1 ? '' : 's'} associated successfully.`
      });
    }
    setMunicipalityCounts((previous) => ({
      ...previous,
      [municipalityId]: (previous[municipalityId] || 0) + successful
    }));
    await loadMunicipalityDocuments(municipalityId, {
      page: documentsByMunicipality[municipalityId]?.pagination.current_page || 1
    });
  };

  return (
    <>
      <Head title="Municipality Document Management" />
      <Toaster />

      <div className="container mx-auto p-6 max-w-7xl">
        <div className="space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <Home className="h-5 w-5 text-blue-600" />
              <h1 className="text-2xl font-semibold">Municipality Document Management</h1>
            </div>
            <Button asChild variant="outline">
              <Link href="/documents">
                <FileText className="h-4 w-4 mr-2" />
                Back to Documents
              </Link>
            </Button>
          </div>

          <Card>
            <CardContent className="p-6">
              <Input
                placeholder="Search municipalities..."
                value={municipalitySearch}
                onChange={(event) => setMunicipalitySearch(event.target.value)}
              />
            </CardContent>
          </Card>

          <div className="space-y-4">
            {filteredMunicipalities.map((municipality) => {
              const expanded = expandedMunicipalityIds.has(municipality.id);
              const state = documentsByMunicipality[municipality.id];
              const documentCount = municipalityCounts[municipality.id] || 0;

              return (
                <Card key={municipality.id}>
                  <CardHeader>
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <button
                        type="button"
                        className="flex items-center gap-3 text-left"
                        onClick={() => toggleMunicipality(municipality.id)}
                      >
                        {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        <Building2 className="h-5 w-5" />
                        <CardTitle>{municipality.name}</CardTitle>
                        <Badge variant="secondary">{documentCount} documents</Badge>
                      </button>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => router.visit(`/municipalities/${municipality.id}/document_groups`)}
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Manage Groups
                        </Button>
                        <AddDocumentsDialog
                          municipality={municipality}
                          onAssociate={(documentIds) => handleAssociateDocuments(municipality.id, documentIds)}
                        />
                      </div>
                    </div>
                  </CardHeader>

                  {expanded && (
                    <CardContent className="space-y-4">
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Input
                          placeholder="Search this municipality's documents..."
                          value={state?.search || ''}
                          onChange={(event) => {
                            const search = event.target.value;
                            setDocumentsByMunicipality((previous) => ({
                              ...previous,
                              [municipality.id]: {
                                documents: previous[municipality.id]?.documents || [],
                                pagination: previous[municipality.id]?.pagination || emptyPagination(),
                                search,
                                loading: previous[municipality.id]?.loading || false
                              }
                            }));
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              loadMunicipalityDocuments(municipality.id, { page: 1 });
                            }
                          }}
                        />
                        <Button variant="outline" onClick={() => loadMunicipalityDocuments(municipality.id, { page: 1 })}>
                          <Search className="h-4 w-4 mr-2" />
                          Search
                        </Button>
                      </div>

                      {state?.loading ? (
                        <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Loading documents...
                        </div>
                      ) : (
                        <MunicipalityDocumentsTable
                          municipality={municipality}
                          documents={state?.documents || []}
                          onDisassociate={(documentId, documentName) =>
                            handleDisassociateDocument(municipality.id, municipality.name, documentId, documentName)
                          }
                        />
                      )}

                      {state?.pagination && !state.loading && (
                        <PaginatedTableControls
                          currentPage={state.pagination.current_page}
                          totalPages={state.pagination.total_pages}
                          perPage={state.pagination.per_page}
                          totalCount={state.pagination.total_count}
                          onPageChange={(page) => loadMunicipalityDocuments(municipality.id, { page })}
                          onPerPageChange={(perPage) => loadMunicipalityDocuments(municipality.id, { page: 1, perPage })}
                        />
                      )}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

function MunicipalityDocumentsTable({
  municipality,
  documents,
  onDisassociate
}: {
  municipality: Municipality;
  documents: Document[];
  onDisassociate: (documentId: number, documentName: string) => void;
}) {
  if (documents.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <FileText className="h-12 w-12 mx-auto mb-4" />
        No documents found.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Document Name</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="w-[100px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.map((document) => (
          <TableRow key={document.id}>
            <TableCell className="font-medium">{document.name}</TableCell>
            <TableCell>{document.description || 'No description'}</TableCell>
            <TableCell>
              <DocumentStatusBadge status={document.status} />
            </TableCell>
            <TableCell>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDisassociate(document.id, document.name)}
                aria-label={`Remove ${document.name}`}
              >
                <Minus className="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function AddDocumentsDialog({
  municipality,
  onAssociate
}: {
  municipality: Municipality;
  onAssociate: (documentIds: number[]) => Promise<void>;
}) {
  const [search, setSearch] = useState('');
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<number[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>(emptyPagination());
  const [loading, setLoading] = useState(false);
  const searchSequencer = useRef(new RequestSequencer());
  const { toast } = useToast();

  const loadDocuments = async (page = 1, perPage = pagination.per_page, searchQuery = search) => {
    setLoading(true);
    const params = new URLSearchParams({
      municipality_id: String(municipality.id),
      page: String(page),
      per_page: String(perPage)
    });
    if (searchQuery.trim()) params.set('search', searchQuery.trim());

    const request = searchSequencer.current.start();
    const result = await fetchJson<{
      documents: Document[];
      pagination: PaginationMeta;
    }>(`/municipality_documents/available_documents.json?${params.toString()}`);

    if (!result.ok) {
      if (request.isCurrent()) {
        toast({
          title: 'Error',
          description: 'Failed to load documents to add.',
          variant: 'destructive'
        });
        setLoading(false);
      }
      return;
    }

    if (!request.isCurrent()) return;

    setDocuments(result.data.documents);
    setPagination(result.data.pagination);
    setLoading(false);
  };

  const toggleDocument = (documentId: number) => {
    setSelectedDocumentIds((previous) =>
      previous.includes(documentId)
        ? previous.filter((id) => id !== documentId)
        : [...previous, documentId]
    );
  };

  const associateSelected = async () => {
    if (selectedDocumentIds.length === 0) return;

    await onAssociate(selectedDocumentIds);
    setSelectedDocumentIds([]);
    await loadDocuments(pagination.current_page);
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open) {
          setSearch('');
          setSelectedDocumentIds([]);
          loadDocuments(1, pagination.per_page, '');
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Documents
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Add Documents to {municipality.name}</DialogTitle>
          <DialogDescription>
            Browse available documents or search by name or description, then select documents to associate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              loadDocuments(1);
            }}
          >
            <Input
              className="min-w-0 flex-1"
              placeholder="Search documents to add..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Button type="submit" variant="outline" className="shrink-0">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
            {search.trim() && (
              <Button
                type="button"
                variant="ghost"
                className="shrink-0"
                onClick={() => {
                  setSearch('');
                  loadDocuments(1, pagination.per_page, '');
                }}
              >
                Clear
              </Button>
            )}
          </form>

          <div className="max-h-96 overflow-y-auto border rounded-md">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading documents...
              </div>
            ) : documents.length > 0 ? (
              <div className="divide-y">
                {documents.map((document) => (
                  <label key={document.id} className="flex items-start gap-3 p-3 cursor-pointer">
                    <Checkbox
                      checked={selectedDocumentIds.includes(document.id)}
                      onCheckedChange={() => toggleDocument(document.id)}
                    />
                    <span className="flex-1">
                      <span className="block font-medium">{document.name}</span>
                      <span className="block text-sm text-muted-foreground">{document.description || 'No description'}</span>
                    </span>
                    <DocumentStatusBadge status={document.status} />
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {search.trim()
                  ? 'No documents matched your search.'
                  : 'No documents are available to add to this municipality.'}
              </div>
            )}
          </div>

          {pagination.total_count > 0 && !loading && (
            <PaginatedTableControls
              currentPage={pagination.current_page}
              totalPages={pagination.total_pages}
              perPage={pagination.per_page}
              totalCount={pagination.total_count}
              onPageChange={(page) => loadDocuments(page)}
              onPerPageChange={(perPage) => loadDocuments(1, perPage)}
            />
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSelectedDocumentIds([])} disabled={selectedDocumentIds.length === 0}>
              Clear Selection
            </Button>
            <Button onClick={associateSelected} disabled={selectedDocumentIds.length === 0}>
              Associate {selectedDocumentIds.length} Document{selectedDocumentIds.length === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

